import MessageInspirationPanel, {
  hasMessageInspirationContent,
} from '@/components/forms/MessageInspirationPanel'
import { getMessageInspirationPreviewEntries } from '@/components/forms/messageInspirationPreviewEntries'
import ImageLightboxGallery from '@/components/media/ImageLightboxGallery'
import ImageUploader from '@/components/media/ImageUploader'
import { InfoNotice } from '@/components/patterns/info-notice'
import { EmptyState } from '@/components/patterns/empty-state'
import UploadedMediaPreviewTile from '@/components/media/UploadedMediaPreviewTile'
import SaveBar from '@/components/forms/SaveBar'
import { Button } from '@/components/ui/button'
import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import {
  DetailCard,
  DetailIconBadge,
  DetailWell,
  detailSurfaceActionClass,
} from '@/components/patterns/detail-page'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Typography } from '@/components/ui/typography'
import { Textarea } from '@/components/ui/textarea'
import { usePathHelper } from '@/hooks/usePathHelper'
import { Can, useIsReadOnly } from '@/lib/casl'
import dayjs from '@/lib/dayjs'
import { EventLeadType } from '@/lib/enums/EventLeadType'
import { MediaUploadUsage } from '@/lib/enums/MediaUploadUsage'
import { MessageInspirationSourceType } from '@/lib/enums/MessageInspirationSourceType'
import { PhotoManagerMode } from '@/lib/photo-manager-config'
import { useAppForm } from '@/lib/form'
import { mergeEditedMedia } from '@/lib/media-editing'
import {
  useCreateMessageMutation,
  useUpdateMessageMutation,
} from '@/lib/services/messages/messages.query'
import { messageDetailsSchema, messageSchema } from '@/lib/services/messages/messages.schema'
import { cn, getErrorMessage } from '@/lib/utils'
import { useNavigate } from '@tanstack/react-router'
import {
  ImagePlus,
  Info,
  MessageCircleHeart,
  NotebookPen,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react'
import { useEffect, useId, useState } from 'react'
import { toast } from 'sonner'

const baseEventTypeOptions = Object.values(EventLeadType).map((eventType) => ({
  id: eventType.Value,
  name: eventType.Name,
}))

const inspirationSourceOptions = [
  { id: 'none', name: 'No inspiration to share right now' },
  { id: MessageInspirationSourceType.Upload.Value, name: 'Upload Images' },
  { id: MessageInspirationSourceType.PinterestBoard.Value, name: 'Pinterest Board Link' },
  {
    id: MessageInspirationSourceType.GooglePhotosAlbum.Value,
    name: 'Google Photos Album Link',
  },
  { id: MessageInspirationSourceType.IcloudAlbum.Value, name: 'iCloud Shared Album Link' },
]

const baseEventTypeValues = new Set(baseEventTypeOptions.map((eventType) => eventType.id))

function normalizeEventType(value) {
  if (!value) return ''

  const normalized = String(value).trim().toLowerCase().replaceAll(' ', '_')
  if (baseEventTypeValues.has(normalized)) {
    return normalized
  }

  return String(value)
}

function getEventTypeOptions(currentValue) {
  const normalizedValue = normalizeEventType(currentValue)
  if (!normalizedValue || baseEventTypeValues.has(normalizedValue)) {
    return baseEventTypeOptions
  }

  return [...baseEventTypeOptions, { id: normalizedValue, name: `${currentValue} (Legacy)` }]
}

function resolveInitialIsEvent(initialData) {
  if (initialData?.is_event === true) {
    return true
  }

  return Boolean(
    initialData?.event_type ||
    initialData?.event_type_other ||
    initialData?.event_date ||
    initialData?.event_location
  )
}

const emptyMessage = {
  name: '',
  email: '',
  phone: '',
  event_type: '',
  event_type_other: '',
  message: '',
  is_event: false,
  event_date: '',
  event_location: '',
  inspiration_source_type: 'none',
  inspiration_source_url: '',
  inspiration_media: [],
}

function getMessageFormValues(message) {
  const { internal_notes: _internalNotes, ...messageData } = message ?? {}
  const inspirationSourceType = message?.inspiration_source_type || 'none'

  return {
    ...emptyMessage,
    ...(message?.id ? messageData : {}),
    is_event: resolveInitialIsEvent(message),
    event_type: normalizeEventType(message?.event_type),
    event_date: message?.event_date ?? '',
    inspiration_source_type: inspirationSourceType,
    inspiration_source_url: message?.inspiration_source_url ?? '',
    inspiration_media: Array.isArray(message?.inspiration_media) ? message.inspiration_media : [],
  }
}

function getInspirationUrlLabel(sourceType) {
  switch (sourceType) {
    case MessageInspirationSourceType.PinterestBoard.Value:
      return 'Pinterest Board Link'
    case MessageInspirationSourceType.GooglePhotosAlbum.Value:
      return 'Google Photos Album Link'
    case MessageInspirationSourceType.IcloudAlbum.Value:
      return 'iCloud Shared Album Link'
    default:
      return 'Album Link'
  }
}

function getInspirationUrlPlaceholder(sourceType) {
  switch (sourceType) {
    case MessageInspirationSourceType.PinterestBoard.Value:
      return 'https://www.pinterest.com/your-board/example-board/'
    case MessageInspirationSourceType.GooglePhotosAlbum.Value:
      return 'https://photos.google.com/share/...'
    case MessageInspirationSourceType.IcloudAlbum.Value:
      return 'https://share.icloud.com/photos/...'
    default:
      return 'https://'
  }
}

function inspirationSourceRequiresUrl(sourceType) {
  return [
    MessageInspirationSourceType.PinterestBoard.Value,
    MessageInspirationSourceType.GooglePhotosAlbum.Value,
    MessageInspirationSourceType.IcloudAlbum.Value,
  ].includes(sourceType)
}

function inspirationSourceRequiresUploads(sourceType) {
  return sourceType === MessageInspirationSourceType.Upload.Value
}

function getInspirationMediaIds(media) {
  return Array.isArray(media)
    ? media.map((item) => Number(item?.id)).filter((id) => Number.isFinite(id) && id > 0)
    : []
}

function normalizeInternalNotes(rawNotes, fallbackTimestamp) {
  if (Array.isArray(rawNotes)) {
    return rawNotes
      .filter((noteEntry) => noteEntry && typeof noteEntry.note === 'string')
      .map((noteEntry) => ({
        note: noteEntry.note.trim(),
        timestamp: noteEntry.timestamp || fallbackTimestamp || dayjs().toISOString(),
      }))
      .filter((noteEntry) => noteEntry.note.length > 0)
  }

  if (typeof rawNotes === 'string' && rawNotes.trim()) {
    return [
      {
        note: rawNotes.trim(),
        timestamp: fallbackTimestamp || dayjs().toISOString(),
      },
    ]
  }

  return []
}

function formatNoteTimestamp(timestamp) {
  const parsed = dayjs(timestamp)
  if (!parsed.isValid()) {
    return 'Unknown time'
  }

  return parsed.format('MMM D, YYYY h:mm A')
}

function resolveMessageEventTypeLabel(message) {
  const eventType = normalizeEventType(message?.event_type)

  if (eventType === EventLeadType.Other.Value) {
    return message?.event_type_other || 'Other'
  }

  return baseEventTypeOptions.find((option) => option.id === eventType)?.name || message?.event_type
}

function formatMessageEventDate(value) {
  if (!value) {
    return ''
  }

  const parsed = dayjs(value)

  return parsed.isValid() ? parsed.format('MMM D, YYYY') : String(value)
}

function MessageDisplayField({ label, value, className, multiline = false }) {
  const displayValue = typeof value === 'string' ? value.trim() : value
  const hasValue = Boolean(displayValue)

  return (
    <div className={className}>
      <Typography variant="label" tone="muted" className="block">
        {label}
      </Typography>
      <div
        className={cn(
          'mt-2 text-sm font-semibold text-foreground',
          multiline && 'whitespace-pre-wrap break-words leading-6'
        )}
      >
        {hasValue ? displayValue : <span className="font-normal text-muted-foreground">None</span>}
      </div>
    </div>
  )
}

function MessageCardHeader({ action }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex min-w-0 items-center gap-3">
        <DetailIconBadge icon={MessageCircleHeart} tone="periwinkle" />
        <div className="min-w-0">
          <Typography variant="cardTitle">Customer Message</Typography>
        </div>
      </div>
      {action}
    </div>
  )
}

function MessageReadOnlyDetails({ message }) {
  const isEvent = resolveInitialIsEvent(message)
  const messageText =
    typeof message?.message === 'string' ? message.message.trim() : message?.message

  return (
    <div className="space-y-5">
      <div className="rounded-md border border-status-info-border bg-status-info-background p-4">
        <Typography variant="label" tone="info" className="block">
          How can we help?
        </Typography>
        <p className="mt-3 whitespace-pre-wrap break-words text-base leading-7 font-semibold text-foreground">
          {messageText || (
            <span className="font-normal text-muted-foreground">No message provided.</span>
          )}
        </p>
      </div>
      {isEvent ? (
        <div className="space-y-4 border-t border-border/60 pt-5">
          <Typography variant="eyebrow">Event Details</Typography>
          <div className="grid grid-cols-6 gap-x-6 gap-y-5">
            <MessageDisplayField
              label="Event Type"
              value={resolveMessageEventTypeLabel(message)}
              className="col-span-6 md:col-span-2"
            />
            <MessageDisplayField
              label="Event Date"
              value={formatMessageEventDate(message?.event_date)}
              className="col-span-6 md:col-span-2"
            />
            <MessageDisplayField
              label="Event Location"
              value={message?.event_location}
              className="col-span-6 md:col-span-2"
            />
          </div>
        </div>
      ) : null}
      {hasMessageInspirationContent(message) ? <MessageInspirationPanel message={message} /> : null}
    </div>
  )
}

function MessageInspirationEditor({ form, values, isReadOnly }) {
  const [isUploaderOpen, setIsUploaderOpen] = useState(false)
  const selectedSource = values.inspiration_source_type || 'none'
  const selectedMedia = Array.isArray(values.inspiration_media) ? values.inspiration_media : []
  const previewEntries = getMessageInspirationPreviewEntries(selectedMedia)
  const previewItems = previewEntries.map((entry) => entry.previewItem)
  const canAddMoreImages = selectedMedia.length < 10

  const handleSourceChange = (sourceType) => {
    if (!inspirationSourceRequiresUrl(sourceType)) {
      form.setFieldValue('inspiration_source_url', '')
    }

    if (!inspirationSourceRequiresUploads(sourceType)) {
      form.setFieldValue('inspiration_media', [])
    }
  }

  const handleUploadSuccess = (_, response) => {
    const uploadedMedia = response?.body
    if (!uploadedMedia?.id) {
      return
    }

    form.setFieldValue('inspiration_media', [...selectedMedia, uploadedMedia].slice(0, 10))
    setIsUploaderOpen(false)
  }

  const removeMedia = (mediaId) => {
    form.setFieldValue(
      'inspiration_media',
      selectedMedia.filter((media) => media.id !== mediaId)
    )
  }

  const replaceMedia = (mediaIndex, editedMedia) => {
    const nextMedia = [...selectedMedia]
    nextMedia[mediaIndex] = mergeEditedMedia(nextMedia[mediaIndex], editedMedia)
    form.setFieldValue('inspiration_media', nextMedia)
  }

  return (
    <div className="col-span-6 space-y-4 rounded-xl border border-border/60 bg-muted/20 p-4">
      <div className="space-y-1">
        <Typography variant="eyebrow">Inspiration</Typography>
        <Typography variant="supporting" className="block">
          Share an album link or upload photos for this inquiry.
        </Typography>
      </div>
      <form.AppField name="inspiration_source_type">
        {(field) => (
          <field.SelectInput
            label="Inspiration"
            options={inspirationSourceOptions}
            disabled={isReadOnly}
            onValueChange={handleSourceChange}
          />
        )}
      </form.AppField>

      {inspirationSourceRequiresUrl(selectedSource) ? (
        <form.AppField name="inspiration_source_url">
          {(field) => (
            <field.TextInput
              type="url"
              label={getInspirationUrlLabel(selectedSource)}
              placeholder={getInspirationUrlPlaceholder(selectedSource)}
              disabled={isReadOnly}
            />
          )}
        </form.AppField>
      ) : null}

      {inspirationSourceRequiresUploads(selectedSource) ? (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <Typography variant="label" className="block">
                Uploaded Photos
              </Typography>
              <Typography variant="supporting" className="block">
                {selectedMedia.length} of 10 image{selectedMedia.length === 1 ? '' : 's'}
              </Typography>
            </div>
            {!isReadOnly && canAddMoreImages ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsUploaderOpen(true)}
              >
                <ImagePlus className="size-4" />
                Upload Image
              </Button>
            ) : null}
          </div>
          {selectedMedia.length > 0 ? (
            <ImageLightboxGallery items={previewItems} className="contents">
              {({ getTriggerProps }) => (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {previewEntries.map((entry, index) => (
                    <UploadedMediaPreviewTile
                      key={entry.media.id ?? `${entry.media.url}-${index}`}
                      media={entry.media}
                      alt={entry.alt}
                      caption={entry.caption}
                      onRemove={!isReadOnly ? () => removeMedia(entry.media.id) : null}
                      onEditComplete={
                        !isReadOnly
                          ? (editedMedia) => replaceMedia(entry.mediaIndex, editedMedia)
                          : null
                      }
                      removeLabel={`Remove ${entry.media.name || 'inspiration image'}`}
                      editUploadUsage={MediaUploadUsage.ImageStandard.Value}
                      wrapperClassName="w-full"
                      frameClassName="w-full overflow-hidden rounded-xl border border-border/60 bg-background"
                      imageClassName="aspect-square w-full object-cover"
                      lightboxTriggerProps={getTriggerProps(index, { mode: 'button' })}
                    />
                  ))}
                </div>
              )}
            </ImageLightboxGallery>
          ) : (
            <EmptyState size="compact" title="No inspiration photos uploaded yet." />
          )}
          {!isReadOnly ? (
            <ImageUploader
              id="message-inspiration-uploader"
              uploadUsage={MediaUploadUsage.ImageStandard.Value}
              photoManagerMode={PhotoManagerMode.UploadOnly}
              open={isUploaderOpen}
              onRequestClose={() => setIsUploaderOpen(false)}
              onUploadSuccess={handleUploadSuccess}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function clearInspirationFields(form) {
  form.setFieldValue('inspiration_source_type', 'none')
  form.setFieldValue('inspiration_source_url', '')
  form.setFieldValue('inspiration_media', [])
}

function MessageEditorFields({
  form,
  isEditing,
  isReadOnly,
  eventTypeOptions,
  maxEventDate,
  showContactFields = true,
}) {
  return (
    <div className="grid gap-6">
      <div className="grid grid-cols-6 gap-6">
        {showContactFields ? (
          <>
            <div className="col-span-6 md:col-span-3">
              <form.AppField name="name">
                {(field) => <field.TextInput label="Name" disabled={isReadOnly} />}
              </form.AppField>
            </div>
            <div className="col-span-6 md:col-span-3">
              <form.AppField name="email">
                {(field) => <field.EmailInput label="Email" disabled={isReadOnly} />}
              </form.AppField>
            </div>
            <div className="col-span-6 md:col-span-3">
              <form.AppField name="phone">
                {(field) => <field.PhoneInput label="Phone" disabled={isReadOnly} />}
              </form.AppField>
            </div>
          </>
        ) : null}
        <div className="col-span-6 md:col-span-3">
          <form.AppField name="is_event">
            {(field) => (
              <field.SwitchInput
                label="Is this for an event?"
                className="pt-7"
                disabled={isReadOnly}
                onCheckedChange={(isChecked) => {
                  field.handleChange(isChecked)

                  if (!isChecked) {
                    form.setFieldValue('event_type', '')
                    form.setFieldValue('event_type_other', '')
                    form.setFieldValue('event_date', '')
                    form.setFieldValue('event_location', '')
                    clearInspirationFields(form)
                  }
                }}
              />
            )}
          </form.AppField>
        </div>
        <form.Subscribe selector={(state) => state.values}>
          {(values) =>
            values.is_event ? (
              <>
                <div className="col-span-6 md:col-span-3">
                  <form.AppField name="event_type">
                    {(field) => (
                      <field.SelectInput
                        label="Event Type"
                        options={eventTypeOptions}
                        disabled={isReadOnly}
                        onValueChange={(nextEventType) => {
                          if (nextEventType !== EventLeadType.Other.Value) {
                            form.setFieldValue('event_type_other', '')
                          }
                        }}
                      />
                    )}
                  </form.AppField>
                </div>
                {values.event_type === EventLeadType.Other.Value ? (
                  <div className="col-span-6 md:col-span-3">
                    <form.AppField name="event_type_other">
                      {(field) => (
                        <field.TextInput label="Other Event Type" disabled={isReadOnly} />
                      )}
                    </form.AppField>
                  </div>
                ) : null}
                <div className="col-span-6 md:col-span-3">
                  <form.AppField name="event_date">
                    {(field) => (
                      <field.DatePicker
                        label="Event Date"
                        placeholder="Pick event date"
                        format="MMM D, YYYY"
                        maxDate={maxEventDate}
                        className="h-9 w-full"
                      />
                    )}
                  </form.AppField>
                </div>
                <div className="col-span-6 md:col-span-3">
                  <form.AppField name="event_location">
                    {(field) => <field.TextInput label="Event Location" disabled={isReadOnly} />}
                  </form.AppField>
                </div>
                <MessageInspirationEditor form={form} values={values} isReadOnly={isReadOnly} />
              </>
            ) : null
          }
        </form.Subscribe>
        <div className="col-span-6">
          <form.AppField name="message">
            {(field) => (
              <field.TextareaInput
                label="How can we help?"
                rows={isEditing ? undefined : 6}
                disabled={isReadOnly}
                className={isEditing ? 'pretty-scrollbar min-h-36' : 'pretty-scrollbar'}
              />
            )}
          </form.AppField>
        </div>
      </div>
    </div>
  )
}

function buildMessageSubmitPayload(value, { includeContactFields = true } = {}) {
  const {
    email: _email,
    inspiration_media: _inspirationMedia,
    name: _name,
    phone: _phone,
    ...messageOnlyValue
  } = value
  const contactValues = includeContactFields
    ? {
        name: value.name,
        email: value.email,
        phone: value.phone,
      }
    : {}
  const selectedSourceType =
    value.is_event && value.inspiration_source_type && value.inspiration_source_type !== 'none'
      ? value.inspiration_source_type
      : null

  return {
    ...contactValues,
    ...messageOnlyValue,
    event_type: value.is_event ? value.event_type || null : null,
    event_type_other:
      value.is_event && value.event_type === EventLeadType.Other.Value
        ? value.event_type_other || null
        : null,
    event_date: value.is_event ? value.event_date || null : null,
    event_location: value.is_event ? value.event_location || null : null,
    inspiration_source_type: selectedSourceType,
    inspiration_source_url:
      selectedSourceType && inspirationSourceRequiresUrl(selectedSourceType)
        ? value.inspiration_source_url || null
        : null,
    inspiration_media_ids:
      selectedSourceType && inspirationSourceRequiresUploads(selectedSourceType)
        ? getInspirationMediaIds(value.inspiration_media)
        : [],
  }
}

const MessageForm = ({ initialData }) => {
  const path = usePathHelper()
  const id = useId()
  const navigate = useNavigate()
  const { isReadOnly } = useIsReadOnly()

  const createMessageMutation = useCreateMessageMutation()
  const updateMessageMutation = useUpdateMessageMutation()
  const isEditing = Boolean(initialData?.id)
  const [isMessageEditorOpen, setIsMessageEditorOpen] = useState(false)
  const [lastSavedMessage, setLastSavedMessage] = useState(null)
  const maxEventDate = dayjs().add(24, 'month').toDate()
  const displayMessage =
    isEditing &&
    lastSavedMessage?.source === initialData &&
    lastSavedMessage?.message?.id === initialData?.id
      ? { ...initialData, ...lastSavedMessage.message }
      : initialData

  const defaultValues = getMessageFormValues(displayMessage)

  const eventTypeOptions = getEventTypeOptions(defaultValues.event_type)
  const form = useAppForm({
    defaultValues,
    validators: {
      onSubmit: isEditing ? messageDetailsSchema : messageSchema,
      onSubmitAsync: async ({ value, formApi }) => {
        const mutation = isEditing ? updateMessageMutation : createMessageMutation
        const payload = buildMessageSubmitPayload(value, { includeContactFields: !isEditing })

        try {
          const response = await mutation.mutateAsync(
            isEditing ? { messageId: initialData.id, ...payload } : payload
          )

          toast.success(`Message ${isEditing ? 'updated' : 'created'} successfully`)

          if (isEditing) {
            const savedMessage = {
              ...initialData,
              ...payload,
              ...response,
              id: response?.id ?? initialData.id,
              internal_notes: Array.isArray(response?.internal_notes)
                ? response.internal_notes
                : initialData.internal_notes,
            }

            setLastSavedMessage({ source: initialData, message: savedMessage })
            formApi.reset(getMessageFormValues(savedMessage))
            setIsMessageEditorOpen(false)
          } else {
            requestAnimationFrame(() => formApi.reset())
          }

          if (!isEditing && response?.id) {
            return navigate({ ...path.messages.show(response.id).link, ignoreBlocker: true })
          }
        } catch (error) {
          toast.error(`Failed to ${isEditing ? 'update' : 'create'} message`, {
            description: getErrorMessage(error),
          })
          if (error?.response?.data?.errors) {
            return { fields: error.response.data.errors }
          }
        }
      },
    },
  })

  const openMessageCardEditor = () => {
    form.reset(getMessageFormValues(displayMessage))
    setIsMessageEditorOpen(true)
  }

  const closeMessageCardEditor = () => {
    form.reset(getMessageFormValues(displayMessage))
    setIsMessageEditorOpen(false)
  }

  if (!isEditing) {
    return (
      <div className="w-full space-y-6">
        <form.AppForm id={id} aria-label="Message Form">
          <div className="space-y-4">
            <form.FormError />
            <form.Form className="space-y-6">
              <DetailCard accent="periwinkle" contentClassName="space-y-6 p-5">
                <MessageCardHeader />
                <MessageEditorFields
                  form={form}
                  isEditing={false}
                  isReadOnly={isReadOnly}
                  eventTypeOptions={eventTypeOptions}
                  maxEventDate={maxEventDate}
                  showContactFields
                />
              </DetailCard>

              {!isReadOnly ? (
                <Can do="create" on="messages">
                  <SaveBar submitButtonText="Save" />
                </Can>
              ) : null}
            </form.Form>
          </div>
        </form.AppForm>
      </div>
    )
  }

  return (
    <div className="w-full space-y-6">
      <DetailCard accent="periwinkle" contentClassName="space-y-6 p-5">
        <MessageCardHeader
          action={
            !isReadOnly ? (
              <Can do="edit" on="messages">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className={cn(detailSurfaceActionClass, 'shrink-0')}
                  aria-label="Edit message details"
                  onClick={openMessageCardEditor}
                >
                  <Pencil className="size-4" />
                </Button>
              </Can>
            ) : null
          }
        />
        <MessageReadOnlyDetails message={displayMessage} />
      </DetailCard>

      <Sheet
        open={isMessageEditorOpen}
        onOpenChange={(open) => {
          if (open) {
            openMessageCardEditor()
          } else {
            closeMessageCardEditor()
          }
        }}
      >
        <SheetContent className="w-full p-0 sm:max-w-xl md:max-w-2xl">
          <form.AppForm id={id} aria-label="Message Form">
            <form.Form className="flex min-h-0 flex-1 flex-col">
              <SheetHeader className="border-b border-border/70 p-6 pr-12">
                <div className="flex items-center gap-3">
                  <DetailIconBadge icon={MessageCircleHeart} tone="periwinkle" />
                  <div className="min-w-0">
                    <SheetTitle>Customer Message</SheetTitle>
                    <SheetDescription>
                      Update the event details, inspiration, and message.
                    </SheetDescription>
                  </div>
                </div>
              </SheetHeader>
              <div className="pretty-scrollbar min-h-0 flex-1 space-y-5 overflow-y-auto p-6">
                <form.FormError />
                <MessageEditorFields
                  form={form}
                  isEditing
                  isReadOnly={isReadOnly}
                  eventTypeOptions={eventTypeOptions}
                  maxEventDate={maxEventDate}
                  showContactFields={false}
                />
              </div>
              {!isReadOnly ? (
                <Can do="edit" on="messages">
                  <SheetFooter className="flex-row justify-end border-t border-border/70 p-4">
                    <Button
                      type="button"
                      variant="outline"
                      disabled={updateMessageMutation.isPending}
                      onClick={closeMessageCardEditor}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={updateMessageMutation.isPending}>
                      {updateMessageMutation.isPending ? 'Updating...' : 'Update'}
                    </Button>
                  </SheetFooter>
                </Can>
              ) : null}
            </form.Form>
          </form.AppForm>
        </SheetContent>
      </Sheet>
    </div>
  )
}

export function MessageInternalNotesPanel({ message, isReadOnly, embedded = false }) {
  return (
    <EventNotesPanel
      messageId={message?.id}
      notes={message?.internal_notes}
      fallbackTimestamp={message?.updated_at ?? message?.created_at}
      isEditing={Boolean(message?.id)}
      isReadOnly={isReadOnly}
      embedded={embedded}
    />
  )
}

function EventNotesPanel({
  messageId,
  notes: rawNotes,
  fallbackTimestamp,
  isEditing,
  isReadOnly,
  embedded,
}) {
  const updateMessageMutation = useUpdateMessageMutation()
  const [draft, setDraft] = useState('')
  const [editingIndex, setEditingIndex] = useState(null)
  const [notes, setNotes] = useState(() => normalizeInternalNotes(rawNotes, fallbackTimestamp))

  useEffect(() => {
    // oxlint-disable-next-line react/set-state-in-effect -- grandfathered
    setNotes(normalizeInternalNotes(rawNotes, fallbackTimestamp))
  }, [fallbackTimestamp, rawNotes])

  const canManageNotes = isEditing && !isReadOnly

  const resetDraft = () => {
    setDraft('')
    setEditingIndex(null)
  }

  const persistNotes = async (nextNotes, { resetAfterSuccess = true } = {}) => {
    if (!messageId) {
      toast.error('Save the message before adding notes')
      return
    }

    try {
      const response = await updateMessageMutation.mutateAsync({
        messageId,
        internal_notes: nextNotes,
      })

      setNotes(normalizeInternalNotes(response?.internal_notes ?? nextNotes, fallbackTimestamp))
      if (resetAfterSuccess) {
        resetDraft()
      }
      toast.success('Notes updated')
    } catch (error) {
      toast.error('Failed to update notes', {
        description: getErrorMessage(error),
      })
    }
  }

  const saveNote = () => {
    const note = draft.trim()
    if (!note) {
      return
    }

    let nextNotes = [...notes]
    if (editingIndex === null) {
      nextNotes = [
        {
          note,
          timestamp: dayjs().toISOString(),
        },
        ...notes,
      ]
    } else {
      nextNotes[editingIndex] = {
        ...nextNotes[editingIndex],
        note,
      }
    }

    void persistNotes(nextNotes)
  }

  const deleteNote = (index) => {
    const shouldResetDraft = editingIndex === index

    if (shouldResetDraft) {
      resetDraft()
    } else if (editingIndex !== null && index < editingIndex) {
      setEditingIndex(editingIndex - 1)
    }

    void persistNotes(
      notes.filter((_, noteIndex) => noteIndex !== index),
      {
        resetAfterSuccess: shouldResetDraft,
      }
    )
  }

  const content = (
    <>
      <div className="flex items-start gap-3">
        <DetailIconBadge icon={NotebookPen} tone="periwinkle" />
        <div className="min-w-0">
          <Typography variant="eyebrow">Internal Notes</Typography>
          <Typography variant="supporting" className="mt-1 block">
            Private notes for your shop only.
          </Typography>
        </div>
      </div>
      <InfoNotice title="Notes stay private" tone="info" compact icon={<Info />}>
        Adding a note here will not email or text the customer.
      </InfoNotice>
      <Textarea
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        placeholder="Private shop note..."
        rows={3}
        disabled={!canManageNotes || updateMessageMutation.isPending}
      />
      <div className="flex justify-end gap-2">
        {editingIndex !== null ? (
          <Button variant="outline" size="sm" onClick={resetDraft}>
            Cancel
          </Button>
        ) : null}
        <Button
          size="sm"
          onClick={saveNote}
          disabled={!canManageNotes || !draft.trim() || updateMessageMutation.isPending}
        >
          <Plus className="size-4" />
          {editingIndex === null ? 'Add Note' : 'Save Note'}
        </Button>
      </div>
      <div className="space-y-3">
        {notes.length === 0 ? (
          <Typography variant="supporting">
            {canManageNotes
              ? 'No internal notes yet.'
              : 'Save this message first to start keeping private notes for your shop.'}
          </Typography>
        ) : (
          notes.map((noteEntry, index) => (
            <DetailWell key={`${noteEntry.timestamp}-${index}`}>
              <p className="whitespace-pre-wrap text-sm leading-6">{noteEntry.note}</p>
              <div className="mt-3 flex items-center justify-between gap-2">
                <Typography variant="supporting">
                  {formatNoteTimestamp(noteEntry.timestamp)}
                </Typography>
                {canManageNotes ? (
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon-xs"
                      aria-label="Edit note"
                      onClick={() => {
                        setDraft(noteEntry.note)
                        setEditingIndex(index)
                      }}
                    >
                      <Pencil className="size-4" />
                    </Button>
                    <ConfirmationDialog
                      variant="destructive"
                      title="Delete note"
                      description="Are you sure you want to delete this internal note?"
                      confirmText="Delete"
                      pendingText="Deleting..."
                      isPending={updateMessageMutation.isPending}
                      onConfirm={() => deleteNote(index)}
                      trigger={
                        <Button variant="ghostDestructive" size="icon-xs" aria-label="Delete note">
                          <Trash2 className="size-4" />
                        </Button>
                      }
                    />
                  </div>
                ) : null}
              </div>
            </DetailWell>
          ))
        )}
      </div>
    </>
  )

  if (embedded) {
    return <div className="space-y-4 p-5">{content}</div>
  }

  return (
    <DetailCard accent="personalTouches" contentClassName="space-y-4 p-5">
      {content}
    </DetailCard>
  )
}

export default MessageForm
